import { useState, useEffect } from "react";
import ModeSelect from "./components/ModeSelect";
import FileUpload from "./components/FileUpload";
import VesselSearch from "./components/VesselSearch";
import MapView from "./components/MapView";
import StatusBar from "./components/StatusBar";
import LicenseGate from "./components/LicenseGate";
import DateRangePicker from "./components/DateRangePicker";
import ExportPanel from "./components/ExportPanel";
import { consolidate, filterByDateRange } from "./modules/consolidator";
import { isUnlocked } from "./modules/licenseGate";
import "./App.css";

/*
  App.jsx — Root component (version 1.4)

  Modes:
  - null     : mode selection screen
  - "vessel" : enter vessel target first, then upload (safe for any file size)
  - "explore": upload directly, see all vessels (small files only, warned)
  - "multi"  : Pro only — multi-file consolidation with vessel target required

  Free tier:  vessel + explore
  Pro tier:   vessel + explore + multi
*/

// Warn before rendering datasets larger than this
const MAX_RENDER_RECORDS = 100000;

export default function App() {
  // null = mode picker, "vessel" | "explore" | "multi" = active mode
  const [mode, setMode] = useState(null);

  const [vesselTargets, setVesselTargets] = useState([]);
  const [consolidatedRecords, setConsolidatedRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [percent, setPercent] = useState(null);
  const [unlocked, setUnlocked] = useState(isUnlocked());
  const [renderWarningDismissed, setRenderWarningDismissed] = useState(false);

  function handleModeSelect(selected) {
    // Reset all data when switching modes
    setMode(selected);
    setVesselTargets([]);
    setConsolidatedRecords([]);
    setFilteredRecords([]);
    setDateRange({ startDate: "", endDate: "" });
    setStatus("");
    setPercent(null);
    setLoading(false);
  }

  function handleSingleFile(records) {
    setConsolidatedRecords(records);
    setFilteredRecords(records);
    setDateRange(extractDateRange(records));
    setStatus("");
  }

  function handleMultiFile(arrayOfArrays) {
    const merged = consolidate(arrayOfArrays);
    setConsolidatedRecords(merged);
    setFilteredRecords(merged);
    setDateRange(extractDateRange(merged));
    setStatus("");
  }

  function extractDateRange(records) {
    if (!records || records.length === 0) return { startDate: "", endDate: "" };

    let minTime = Infinity;
    let maxTime = -Infinity;

    for (const r of records) {
      const t = new Date(r.timestamp).getTime();
      if (!isNaN(t)) {
        if (t < minTime) minTime = t;
        if (t > maxTime) maxTime = t;
      }
    }

    if (minTime === Infinity) return { startDate: "", endDate: "" };

    const toLocal = (d) => {
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    return {
      startDate: toLocal(new Date(minTime)),
      endDate: toLocal(new Date(maxTime)),
    };
  }

  function handleDateRangeChange(range) {
    setDateRange(range);
    const start = range.startDate ? new Date(range.startDate) : null;
    const end = range.endDate ? new Date(range.endDate) : null;
    const result = filterByDateRange(consolidatedRecords, start, end);
    setFilteredRecords(result);
    if (result.length === 0 && (start || end)) {
      setStatus("No records in that date range. Try widening it.");
    } else {
      setStatus("");
    }
  }

  function handleUnlock() {
    setUnlocked(true);
  }

  const exportName = vesselTargets && vesselTargets.length > 0
    ? vesselTargets.map((t) => t.mmsi || t.vesselName || "vessel").join("_")
    : "all-vessels";

  const hasResults = filteredRecords.length > 0;

  // Warn before rendering large datasets
  const isLargeDataset = filteredRecords.length > MAX_RENDER_RECORDS;
  const showRenderWarning = isLargeDataset && !renderWarningDismissed;

  // Reset warning on new records so a fresh upload re-warns if still large
  useEffect(() => {
    setRenderWarningDismissed(false);
  }, [filteredRecords]);

  // Vessel search shown in vessel and multi modes, not explore
  const showVesselSearch = mode === "vessel" || mode === "multi";

  // Only multi mode allows multiple files
  const allowMultiFile = mode === "multi";

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-left">
          <h1 className="app-title">AIS<span>sift</span></h1>
          <p className="app-tagline">Drop in any AIS file. Pick your vessel. Get clean data back.</p>
        </div>
        <div className="app-header-right">
          {mode && (
            <button className="app-change-mode" onClick={() => handleModeSelect(null)}>
              ← Change mode
            </button>
          )}
          <span className={`app-header-badge ${unlocked ? "app-header-badge--pro" : "app-header-badge--free"}`}>
            {unlocked ? "Pro" : "Free"}
          </span>
        </div>
      </header>

      <main className="app-main">

        {/* License gate — always visible when not unlocked */}
        {!unlocked && (
          <LicenseGate onUnlock={handleUnlock} />
        )}

        {/* Mode selection — shown when no mode is active */}
        {!mode && (
          <ModeSelect onSelect={handleModeSelect} unlocked={unlocked} />
        )}

        {/* Active mode flow */}
        {mode && (
          <>
            {/* Step 1 — Vessel target (vessel and multi modes only) */}
            {showVesselSearch && (
              <VesselSearch
                key={mode}
                onTargetsChange={setVesselTargets}
                unlocked={unlocked}
                mode={mode}
              />
            )}

            {/* Step 2 — Upload */}
            <FileUpload
              setAllRecords={handleSingleFile}
              setAllRecordsMulti={handleMultiFile}
              setStatus={setStatus}
              setLoading={setLoading}
              setPercent={setPercent}
              unlocked={unlocked}
              vesselTargets={vesselTargets}
              mode={mode}
              allowMultiFile={allowMultiFile}
            />

            {/* Status bar */}
            <StatusBar message={status} loading={loading} percent={percent} />

            {/* Date range — Pro only, after results, only when map is visible */}
            {unlocked && hasResults && !showRenderWarning && (
              <DateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={handleDateRangeChange}
              />
            )}

            {/* Max records warning — shown before map renders on large datasets */}
            {hasResults && showRenderWarning && (
              <div className="render-warning">
                <div className="render-warning-left">
                  <p className="render-warning-title">⚠ Large dataset — {filteredRecords.length.toLocaleString()} records</p>
                  <p className="render-warning-desc">
                    Rendering this many records may be slow or freeze your browser.
                    For best results, filter by vessel or narrow the date range first.
                  </p>
                </div>
                <div className="render-warning-actions">
                  <button
                    className="render-warning-btn render-warning-btn--proceed"
                    onClick={() => setRenderWarningDismissed(true)}
                  >
                    Render anyway
                  </button>
                  <button
                    className="render-warning-btn render-warning-btn--back"
                    onClick={() => handleModeSelect(null)}
                  >
                    Start over
                  </button>
                </div>
              </div>
            )}

            {/* Map — only render once warning is dismissed or dataset is small */}
            {hasResults && !showRenderWarning && (
              <MapView records={filteredRecords} mode={mode} />
            )}

            {/* Export — Pro only, only when map is visible */}
            {unlocked && hasResults && !showRenderWarning && (
              <ExportPanel records={filteredRecords} vesselName={exportName} />
            )}

            {/* Upgrade nudge — free only, after results, only when map is visible */}
            {!unlocked && hasResults && !showRenderWarning && (
              <div className="upgrade-nudge">
                <div className="upgrade-nudge-left">
                  <p className="upgrade-nudge-title">Want to do more with this data?</p>
                  <div className="upgrade-nudge-features">
                    <span className="upgrade-nudge-feature">Multiple files & date ranges</span>
                    <span className="upgrade-nudge-feature">CSV export</span>
                    <span className="upgrade-nudge-feature">KML for Google Earth</span>
                    <span className="upgrade-nudge-feature">Up to 5 vessel targets</span>
                  </div>
                </div>
                <a
                  href="https://aissift.lemonsqueezy.com"
                  target="_blank"
                  rel="noreferrer"
                  className="upgrade-nudge-btn"
                >
                  Get Pro — $9/month
                </a>
              </div>
            )}
          </>
        )}

      </main>

      <footer className="app-footer">
        <p>Data is processed locally on your machine. Nothing is uploaded or stored.</p>
      </footer>
    </div>
  );
}
